import { getGreetingPeriod, type GreetingPeriod } from './time-utils';

/// Formats an ingredient quantity for display, appending the unit only when
/// quantityStr is a bare number/fraction (e.g. "0.75" + "oz" -> "0.75 oz").
/// Source data sometimes already embeds the unit in quantityStr (e.g.
/// "3/4 oz" or ".5 oz Lime Juice") -- in that case quantityStr is returned
/// unchanged so the unit isn't duplicated.

export function formatQuantity(
  quantityStr: string | undefined,
  quantityType: string | undefined
): string {
  const isBareNumber = (s: string) => /^[\d.\/\s]+$/.test(s);
  const trimmed = quantityStr?.trim();
  const type = quantityType?.trim();
  if (!trimmed) {
    return type ?? '';
  }
  // Only append quantityType when it looks like an actual unit word (not
  // itself a bare number -- source data sometimes has a stray fraction in
  // quantity_type, which isn't a real unit).
  return isBareNumber(trimmed) && type && !isBareNumber(type)
    ? `${trimmed} ${type}`
    : trimmed;
}

/// Converts an input to a normalized ascii lowercase representation.
/// Handy for making string keys like "creme_de_violette" from "Crème De Violette"
/// Note that this is kinda mediocre ChatGPT-written code and to really be
/// correct in some cases it would require a language model.
/// It *should* be fine for importing some drink categories though.
export function toAsciiLowercase(input: string): string {
  // Normalize accents, remove diacritical marks, and lowercase
  let asciiStr = input
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, '_') // Replace spaces with a single underscore
    .replace(/[^a-z_]/g, ''); // Remove non-alphanumeric characters except underscores

  // Collapse sequences of three or more repeated characters to two
  asciiStr = asciiStr.replace(/(\w)\1{2,}/g, '$1$1');

  // Trim any leading or trailing underscores
  asciiStr = asciiStr.replace(/^_+|_+$/g, '');

  return asciiStr;
}

export function capitalize(input: string): string {
  const firstChar = input.charAt(0).toLocaleUpperCase();
  const rest = input.slice(1);
  return `${firstChar}${rest}`;
}

export function normalizeSpaces(input: string): string {
  return input.trim().replace(/\s+/g, ' ');
}

const GREETING_KEYS = {
  morning: 'greeting.good-morning',
  afternoon: 'greeting.good-afternoon',
  evening: 'greeting.good-evening'
} as const;

export function getTimeBasedGreeting(
  t: (key: (typeof GREETING_KEYS)[GreetingPeriod]) => string,
  options?: { now?: Date; timeZone?: string }
): string {
  return t(GREETING_KEYS[getGreetingPeriod(options?.now, options?.timeZone)]);
}

// Straight up lexical templatization.
export function templatize(
  inputString: string,
  values: Record<string, string>
) {
  const re = /\${(?<varname>\w+)}/g;
  const literals = inputString.matchAll(re);
  let replacedString = inputString;
  for (const match of literals) {
    const [literalString, variableName] = match;
    let replacedValue = values[variableName];
    if (!replacedValue) {
      console.warn(`No value given for "${variableName}"`);
      replacedValue = `undefined`;
    }
    replacedString = replacedString.replaceAll(literalString, replacedValue);
  }
  return replacedString;
}

export function sanitizeSearchQuery(searchString: string): string {
  // Remove special characters and normalize spaces
  return searchString
    .trim()
    .replace(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map(term => `${term}:*`)
    .join(' & ');
}
